/* eslint-disable no-console */
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import cx from "classix";
import { Container, ListaEdificios, Loader } from "..";
import { useAuthContext } from "../../context/auth";
import { log, getOportunidades, updateOportunidad } from "../../utils";
import "./stylesOportunidades.scss";

export const Oportunidades = () => {
  const { oportunidadId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation(); // Obtener state
  const [oportunidadIdentificada, setOportunidadIdentificada] = useState(state?.oportunidad); // Usar state.oportunidad
  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
  const [temporalDataForm, setTemporalDataForm] = useState();
  const [loading, setLoading] = useState(false);

  const { pais } = useAuthContext();

  const methods = useForm();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    setError: setFormError, // Renombrar a setFormError
  } = methods;

  const onSubmit = (data) => {
    const currentDate = new Date().toISOString(); // Obtiene la fecha actual en formato ISO
    const newOportunidad = {
      edificiosparticipantes: data.edificiosSeleccionados,
      edificiostotales: Number(data.edificiosTotales),
      pantallastotales: Number(data.pantallasTotales),
      importe: data.importe,
      estatus: data.estatus,
      mes: data.mes,
      mes_cierre: Number(data.mes_cierre), // Convertir a número para Supabase
      ano: data.ano,
      updated_at: currentDate,
    };

    setTemporalDataForm(newOportunidad);
    setShowModalConfirmacion(true); // Muestra el modal de confirmación
  };

  const saveButtonHandle = async () => {
    try {
      setLoading(true);
      setShowModalConfirmacion(false);
      await updateOportunidad({ id: oportunidadId, oportunidad: temporalDataForm });
      toast.success("La oportunidad se actualizó correctamente");
      setLoading(false);
      navigate("/admin/ventas-detalle");
    } catch (error) {
      if (error.code === "23505") setFormError("nombre", { type: "custom", message: "La oportunidad ya existe" }); // Usar setFormError
      toast.error("Hubo un error al actualizar la oportunidad, prueba de nuevo.");
      log.error("saveButtonHandle", error);
      setLoading(false);
      setShowModalConfirmacion(false);
    }
  };

  const months = [
    { value: "01", label: "ENE" },
    { value: "02", label: "FEB" },
    { value: "03", label: "MAR" },
    { value: "04", label: "ABR" },
    { value: "05", label: "MAY" },
    { value: "06", label: "JUN" },
    { value: "07", label: "JUL" },
    { value: "08", label: "AGO" },
    { value: "09", label: "SET" },
    { value: "10", label: "OCT" },
    { value: "11", label: "NOV" },
    { value: "12", label: "DIC" },
  ];

  const selectOptions = months.map((month) => (
    <option key={month.value} value={month.value}>
      {month.label}
    </option>
  ));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allOportunidades = await getOportunidades({ pais });
        const oportunidadAEditar = allOportunidades.find((op) => op.id === Number(oportunidadId));
        if (oportunidadAEditar) {
          setOportunidadIdentificada(oportunidadAEditar);
        } else {
          toast.error("Oportunidad no encontrada.");
          navigate("/admin/ventas-detalle");
        }
      } catch (error) {
        toast.error("Error al cargar la oportunidad.");
        log.error("fetchData", error);
        navigate("/admin/ventas-detalle");
      }
    };
    if (oportunidadId && !oportunidadIdentificada) {
      fetchData();
    }
  }, [pais, oportunidadId, navigate]);

  useEffect(() => {
    if (oportunidadIdentificada) {
      setValue("cliente", oportunidadIdentificada.cliente);
      setValue("intermediario", oportunidadIdentificada.intermediario);
      setValue("importe", oportunidadIdentificada.importe);
      setValue("estatus", oportunidadIdentificada.estatus);
      setValue("mes", oportunidadIdentificada.mes);
      // Convertir mes_cierre de número a string con dos dígitos
      setValue(
        "mes_cierre",
        oportunidadIdentificada.mes_cierre ? String(oportunidadIdentificada.mes_cierre).padStart(2, "0") : ""
      );
      setValue("edificiosTotales", oportunidadIdentificada.edificiostotales);
      setValue("pantallasTotales", oportunidadIdentificada.pantallastotales);
      setValue("edificiosparticipantes", oportunidadIdentificada.edificiosparticipantes);
      setValue("ano", oportunidadIdentificada.ano);
    }
  }, [oportunidadIdentificada, setValue]);

  return (
    <Container
      className="Container"
      header
      titulo={`EDITAR OPORTUNIDAD #${oportunidadId}: ${oportunidadIdentificada ? oportunidadIdentificada.cliente : ""}`}
    >
      {oportunidadIdentificada ? (
        <FormProvider {...methods}>
          <form className="row g-3 mt-2" onSubmit={handleSubmit(onSubmit)}>
            <div className="col-4 text-start fw-bold">
              <input
                className="form-control input-disabled"
                id="cliente"
                type="text"
                readOnly
                value={oportunidadIdentificada ? oportunidadIdentificada.cliente : ""}
              />
            </div>

            <div className="col-4 text-start fw-bold">
              <input
                className="form-control input-disabled"
                id="intermediario"
                type="text"
                readOnly
                value={oportunidadIdentificada ? oportunidadIdentificada.intermediario : ""}
              />
            </div>

            <div className="col-4 text-start fw-bold">
              <input
                className={cx("form-control", errors?.importe && "is-invalid")}
                id="importe"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                step="0.01"
                {...register("importe", { required: true, min: 0 })}
              />
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>
            <div className="col-4 text-start fw-bold">
              <select
                className={cx("form-control", errors?.estatus && "is-invalid")}
                id="estatus"
                {...register("estatus", { required: true })}
              >
                <option value="">Selecciona un porcentaje</option>
                <option value="100%">100%</option>
                <option value="90%">90%</option>
                <option value="75%">75%</option>
                <option value="50%">50%</option>
                <option value="0%">0%</option>
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>

            <div className="col-4 text-start fw-bold">
              <select
                className={cx("form-control", errors?.mes_cierre && "is-invalid")}
                id="mes_cierre"
                type="text"
                {...register("mes_cierre", { required: true })}
              >
                <option value="">¿Cuándo se cerrará?</option>
                {selectOptions}
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>

            <div className="col-4 text-start fw-bold">
              <select
                className={cx("form-control", errors?.mes && "is-invalid")}
                id="mes"
                type="text"
                {...register("mes", { required: true })}
              >
                <option value="">¿Cuándo se facturará?</option>
                {selectOptions}
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>

            <div className="col-4 text-start fw-bold">
              <select
                className={cx("form-control", errors?.ano && "is-invalid")}
                id="ano"
                {...register("ano", { required: true })}
              >
                <option value="">Selecciona el año</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>

            <div className="col-4 text-start fw-bold">
              <input
                className={cx("form-control", errors?.edificiosTotales && "is-invalid")}
                defaultValue={0}
                disabled
                id="edificiosTotales"
                type="number"
                {...register("edificiosTotales", { required: true })}
              />
            </div>

            <div className="col-4 text-start fw-bold">
              <input
                className={cx("form-control", errors?.pantallasTotales && "is-invalid")}
                defaultValue={0}
                disabled
                id="pantallasTotales"
                type="number"
                {...register("pantallasTotales", { required: true })}
              />
            </div>

            <ListaEdificios edificiosPreseleccionados={oportunidadIdentificada.edificiosparticipantes} />

            <input hidden type="text" {...register("personasTotales")} />
            <input hidden type="text" {...register("edificiosSeleccionados")} />

            <div className="col-4 d-flex justify-content-center align-items-center mx-auto">
              <button className="btn btn-primary mb-5" type="submit" disabled={loading || showModalConfirmacion}>
                {!loading && !showModalConfirmacion ? (
                  "ACTUALIZAR"
                ) : (
                  <>
                    <Spinner size="sm">GUARDANDO...</Spinner>
                    <span> GUARDANDO</span>
                  </>
                )}
              </button>
            </div>
          </form>

          <Modal isOpen={showModalConfirmacion} toggle={() => setShowModalConfirmacion(false)}>
            <ModalHeader>¿Está seguro?</ModalHeader>
            <ModalBody>Se está por actualizar la oportunidad</ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowModalConfirmacion(false)}>
                Cancelar
              </Button>
              <Button color="primary" onClick={saveButtonHandle}>
                Guardar
              </Button>
            </ModalFooter>
          </Modal>
        </FormProvider>
      ) : (
        <Loader />
      )}
    </Container>
  );
};
